import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { API } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import PostPurchaseFeedback, { shouldShowFeedback } from './PostPurchaseFeedback';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 20;      // 40s window — generous for sandbox propagation

/** Mounts on /app/project/:id once and detects ?checkout=success|cancel.
 *  Polls /api/billing/checkout-status until paid, fires confetti, cleans
 *  the URL, refreshes entitlements. Cancelled checkouts show a toast only.
 */
export default function CheckoutReturnHandler() {
    const location = useLocation();
    const navigate = useNavigate();
    const ent = useEntitlements();
    const [status, setStatus] = useState('idle');   // idle | polling | done
    const [feedbackSession, setFeedbackSession] = useState(null);
    const didRun = useRef(false);

    useEffect(() => {
        if (didRun.current) return;
        const params = new URLSearchParams(location.search);
        const checkout = params.get('checkout');
        const sessionId = params.get('session_id');
        if (!checkout) return;
        didRun.current = true;

        // Strip the query string so reloads don't re-trigger.
        const clean = location.pathname;
        navigate(clean, { replace: true });

        if (checkout === 'cancel') {
            toast('Payment cancelled — nothing charged.');
            return;
        }
        if (checkout !== 'success' || !sessionId) return;

        setStatus('polling');
        toast.loading('Confirming payment…', { id: 'checkout-poll' });

        // Unconditionally POST to the demo-simulate endpoint. If the backend
        // is running on a real Stripe key it returns 404 which we swallow —
        // this removes the race where `ent.demoPayments` hasn't loaded yet
        // on a fresh redirect from Stripe (the previous behaviour left the
        // polling spinning for 40s because the txn was never flipped).
        const demoPrime = async () => {
            try {
                await axios.post(
                    `${API}/billing/demo-simulate-payment/${encodeURIComponent(sessionId)}`,
                    {},
                    { withCredentials: true },
                );
            } catch { /* real-stripe-key path → 404 expected, ignore */ }
        };

        let attempts = 0;
        const tick = async () => {
            attempts += 1;
            if (attempts === 1) await demoPrime();
            try {
                const res = await axios.get(
                    `${API}/billing/checkout-status/${encodeURIComponent(sessionId)}`,
                    { withCredentials: true },
                );
                if (res.data.payment_status === 'paid') {
                    const isLifetime = res.data.product_type === 'lifetime_unlock';
                    const msg = isLifetime
                        ? 'Welcome, Surveyor 📐 — unlimited everything, forever.'
                        : 'Property unlocked — welcome to Rectify 🎉';
                    toast.success(msg, { id: 'checkout-poll' });
                    confetti({ particleCount: 120, spread: 80, origin: { y: 0.3 } });
                    await ent?.refresh?.();
                    // Persist a "show feedback for this session" flag across the
                    // page reload below so the prompt lands on the freshly-
                    // rendered page rather than fighting our own reload.
                    if (shouldShowFeedback(sessionId)) {
                        try { sessionStorage.setItem('defectr:fb:pending', sessionId); }
                        catch { /* private mode — feedback simply skipped */ }
                    }
                    // Force a re-render of the property page by reload — simplest
                    // way to pick up server-side entitlement changes everywhere.
                    setTimeout(() => window.location.reload(), 900);
                    setStatus('done');
                    return;
                }
                if (res.data.status === 'expired') {
                    toast.error('Payment session expired. Please try again.', { id: 'checkout-poll' });
                    setStatus('done');
                    return;
                }
                if (attempts < MAX_ATTEMPTS) {
                    setTimeout(tick, POLL_INTERVAL_MS);
                } else {
                    toast('Still confirming — refresh the page in a moment.', { id: 'checkout-poll' });
                    setStatus('done');
                }
            } catch (err) {
                // Log to devtools so we can trace specific failures (401/403/500).
                console.error('[checkout-poll] error', attempts, err?.response?.status, err?.response?.data || err);
                if (attempts < MAX_ATTEMPTS) {
                    setTimeout(tick, POLL_INTERVAL_MS);
                } else {
                    toast.error('Could not verify payment. Email admin@defectr.co.uk if you were charged.', { id: 'checkout-poll' });
                    setStatus('done');
                }
            }
        };
        tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // After a paid checkout reload, surface the post-purchase feedback prompt.
    // Lives in sessionStorage so it crosses the reload boundary, and gets
    // cleared either on submit, dismiss, or as soon as we read it (whichever
    // comes first) so a refresh later doesn't re-prompt the user.
    useEffect(() => {
        let pending = null;
        try { pending = sessionStorage.getItem('defectr:fb:pending'); }
        catch { /* private mode */ }
        if (!pending) return;
        try { sessionStorage.removeItem('defectr:fb:pending'); }
        catch { /* noop */ }
        if (shouldShowFeedback(pending)) {
            const t = setTimeout(() => setFeedbackSession(pending), 1400);
            return () => clearTimeout(t);
        }
    }, []);

    return (
        <>
            {status === 'polling' ? (
                <div className="fixed inset-0 z-[90] bg-white/80 backdrop-blur-sm flex items-center justify-center" data-testid="checkout-polling">
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B]">Confirming payment…</div>
                </div>
            ) : null}
            {feedbackSession && (
                <PostPurchaseFeedback
                    sessionId={feedbackSession}
                    onClose={() => setFeedbackSession(null)}
                />
            )}
        </>
    );
}
